import { GroupWorkOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Divider,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

interface CreateOrUpdateL1FormProps {
  displayName: string;
  description: string;
  onDisplayNameChange: (displayName: string) => void;
  onDescriptionChange: (description: string) => void;
  isEditView?: boolean;
  onCancel: () => void;
  onSubmit: () => Promise<void>;
  isSaveInProgress: boolean;
  isDisabled?: boolean;
}

export const CreateOrUpdateL1Form = ({
  displayName,
  description,
  onDisplayNameChange,
  onDescriptionChange,
  onCancel,
  onSubmit,
  isSaveInProgress,
  isDisabled = false,
  isEditView = false,
}: CreateOrUpdateL1FormProps) => {
  const isSubmitDisabled = !displayName || !description || isDisabled;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    if (isSubmitDisabled) {
      return;
    }
    e.preventDefault();
    onSubmit();
  };

  const header = (
    <Stack direction="row" alignItems="center" justifyContent="space-between">
      <Typography variant="body1">
        {isEditView ? 'Edit Domain' : 'Create Domain'}
      </Typography>
      <Stack direction="row" spacing={1}>
        <Button size="small" variant="outlined" onClick={onCancel}>
          Cancel
        </Button>
        <LoadingButton
          loading={isSaveInProgress}
          size="small"
          variant="contained"
          type="submit"
          disabled={isSubmitDisabled}
        >
          Save
        </LoadingButton>
      </Stack>
    </Stack>
  );

  const fields = (
    <Stack spacing={3}>
      <TextField
        autoFocus
        label="Domain Name"
        fullWidth
        variant="outlined"
        value={displayName}
        onChange={(e) => onDisplayNameChange(e.target.value)}
        InputLabelProps={{ shrink: true }}
        placeholder="Enter a name for this Domain"
        disabled={isDisabled}
        required
      />
      <TextField
        label="Domain Description"
        fullWidth
        variant="outlined"
        multiline
        rows={4}
        value={description}
        placeholder="Describe this Domain and its key features and aspects"
        onChange={(e) => onDescriptionChange(e.target.value)}
        InputLabelProps={{ shrink: true }}
        disabled={isDisabled}
        required
      />
    </Stack>
  );

  const helperInfo = (
    <Stack spacing={2} divider={<Divider />}>
      <Stack>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <GroupWorkOutlined
            sx={{
              fontSize: (theme) => theme.typography.h3.fontSize,
            }}
            color="info"
          />
          <Typography variant="subtitle1" color="text.primary">
            What makes a good Domain Description
          </Typography>
        </Stack>
        <Stack spacing={1} pl={7.5}>
          <Typography variant="caption" color="text.disabled">
            Describe the product area in great detail. More detailed
            descriptions lead to greater feedback coverage. It's useful to
            mention what features and aspects are covered in this domain.
          </Typography>
          <Link
            href="#" // TODO: Add link
            target="_blank"
            rel="noreferrer"
            variant="caption"
            underline="hover"
          >
            Learn More
          </Link>
        </Stack>
      </Stack>
    </Stack>
  );

  return (
    <Stack
      spacing={3}
      component="form"
      onSubmit={handleSubmit}
      sx={{ p: 2, height: '100%' }}
    >
      {header}
      {fields}
      {helperInfo}
    </Stack>
  );
};
